'use client'

import { PRODUCT_IMAGE_SIZE_THUMBNAIL } from '@/feature/product/services/product.constants'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Card } from '@/ui/cards/card/Card'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import type { ProductCardFragment } from '@gql-generated/graphql'
import { getSafeProductName } from '../../services/product.filtering'
import { toProductPage } from '../../services/product.routing'
import { getProductDisplayPrice } from '../../utils/productDisplayPrice'
import { ProductPrice } from '../product-price/ProductPrice.client'
import { ProductTag } from '../product-tag/ProductTag.client'

import { Link } from '@/ui/links/link/Link.client'

export type AddToBasketCardPriceProps = {
  /** The product displayed
   * TODO: Use the right fragment, since this is not the right data
   */
  product: ProductCardFragment
}

export type AddToBasketCardProps = {
  /** The product displayed
   * TODO: Use the right fragment, since this is not the right data
   */
  product: ProductCardFragment

  /** The callback to call when the product is pressed */
  onPress?: () => void
}

export function AddToBasketCard({ product, onPress }: AddToBasketCardProps) {
  const { t } = useTranslation(['generic', 'addToBasketCard'])
  const tag = product.price?.tag
  const productName = getSafeProductName(product.name)
  const displayPrice = getProductDisplayPrice(product)

  return (
    <Card
      radius="sm"
      className="flex flex-row items-start gap-2 desktop:items-center desktop:gap-3"
    >
      <Link
        variant="free"
        underlined="hover"
        href={toProductPage(product.urlKey)}
        className="shrink-0"
        onPress={onPress}
      >
        <CloudflarePictureProduct
          className="size-10 desktop:size-9"
          src={product.mainImage?.set?.[0]?.url}
          alt={product.mainImage?.alt || productName}
          width={PRODUCT_IMAGE_SIZE_THUMBNAIL}
        />
      </Link>
      <div className="flex flex-col items-start gap-1 desktop:flex-row desktop:items-center">
        <div className="flex flex-col items-start gap-1">
          {!!tag && (
            <div className="hidden desktop:block">
              <ProductTag tag={tag} />
            </div>
          )}
          <Link
            variant="free"
            underlined="hover"
            href={toProductPage(product.urlKey)}
            onPress={onPress}
          >
            <span className="flex flex-col gap-1">
              <span className="flex flex-col gap-0.5 text-md">
                {product.brand && (
                  <span
                    className="font-bold uppercase leading-light"
                    data-testid="add-to-basket-card/brand"
                  >
                    {product.brand.name}
                  </span>
                )}
                <span
                  className="line-clamp-2 leading-light"
                  data-testid="add-to-basket-card/product-name"
                >
                  {productName}
                </span>
              </span>
              <span className="hidden text-sm desktop:inline">
                {t('addToBasketCard:refTextWithValue', { ref: product.reference })}
              </span>
            </span>
          </Link>
        </div>
        <ProductPrice
          price={displayPrice.price}
          priceQuantity={displayPrice.priceQuantity}
          discount={{
            price: displayPrice.discountAmount,
            percentage: displayPrice.discountPercentage,
          }}
          type={product.type}
          packaging={product.stock?.packaging}
          size="2xl"
          className="tablet:justify-end tablet:justify-items-end tablet:text-right"
        />
      </div>
    </Card>
  )
}
